import { useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { ProposalTabs } from "@/components/ProposalTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

const EMPTY_STEP = {
  step_label: "",
  match_city_label: "",
  match_route_segment: "",
  match_direction: "",
  match_area: "",
  notes: "",
};

const EMPTY_FORM = {
  id: "",
  name: "",
  description: "",
  steps: [{ ...EMPTY_STEP, step_order: 1 }],
};

const LOCATION_TYPES = ["City", "Highway", "Bypass", "Entry Point", "Exit Point", "Airport Road", "Other"];
const DIRECTIONS = [["", "Any"], ["upstream", "Upstream"], ["downstream", "Downstream"], ["both", "Both"], ["na", "NA"]];

const arrangementCacheKey = "__hoardexaProposalArrangementsCache";
let arrangementDraftCache = null;

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeForm(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    steps: asArray(form.steps).map((step, index) => ({
      ...step,
      step_order: index + 1,
      step_label: step.step_label.trim(),
      match_city_label: step.match_city_label.trim(),
      match_route_segment: step.match_route_segment.trim(),
      match_area: step.match_area.trim(),
      notes: step.notes.trim(),
    })),
  };
}

export function ProposalArrangements() {
  const [presets, setPresets] = useState(() => globalThis[arrangementCacheKey] || []);
  const [form, setForm] = useState(() => arrangementDraftCache || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedPreset = useMemo(() => presets.find((preset) => preset.id === form.id), [form.id, presets]);

  function loadPresets(force = false) {
    if (!force && globalThis[arrangementCacheKey]) {
      setPresets(globalThis[arrangementCacheKey]);
      return;
    }
    setLoading(true);
    api.get("/proposals/arrangements", { skipGlobalLoader: true })
      .then(({ data }) => {
        globalThis[arrangementCacheKey] = asArray(data);
        setPresets(globalThis[arrangementCacheKey]);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load arrangement presets."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadPresets(false); }, []);

  useEffect(() => {
    arrangementDraftCache = form;
  }, [form]);

  function selectPreset(preset) {
    setForm({
      id: preset.id,
      name: preset.name || "",
      description: preset.description || "",
      steps: asArray(preset.steps).length ? preset.steps.map((step, index) => ({ ...EMPTY_STEP, ...step, step_order: index + 1 })) : [{ ...EMPTY_STEP, step_order: 1 }],
    });
  }

  function updateStep(index, patch) {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? { ...step, ...patch } : step),
    }));
  }

  function addStep() {
    setForm((prev) => ({ ...prev, steps: [...prev.steps, { ...EMPTY_STEP, step_order: prev.steps.length + 1 }] }));
  }

  function removeStep(index) {
    setForm((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, step_order: i + 1 })) }));
  }

  function moveStep(index, direction) {
    setForm((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      [steps[index], steps[nextIndex]] = [steps[nextIndex], steps[index]];
      return { ...prev, steps: steps.map((step, i) => ({ ...step, step_order: i + 1 })) };
    });
  }

  async function savePreset() {
    const payload = normalizeForm(form);
    if (!payload.name) {
      toast.error("Preset name is required.");
      return;
    }
    if (payload.steps.some((step) => !step.step_label)) {
      toast.error("Every step needs a label.");
      return;
    }
    setSaving(true);
    try {
      const { data } = form.id
        ? await api.put(`/proposals/arrangements/${form.id}`, payload)
        : await api.post("/proposals/arrangements", payload);
      setPresets((prev) => {
        const next = [data, ...prev.filter((preset) => preset.id !== data.id)];
        globalThis[arrangementCacheKey] = next;
        return next;
      });
      selectPreset(data);
      toast.success("Arrangement preset saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function deletePreset() {
    if (!form.id) return;
    setSaving(true);
    try {
      await api.delete(`/proposals/arrangements/${form.id}`);
      setPresets((prev) => {
        const next = prev.filter((preset) => preset.id !== form.id);
        globalThis[arrangementCacheKey] = next;
        return next;
      });
      setForm(EMPTY_FORM);
      toast.success("Arrangement preset deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout title="Proposal Arrangements" subtitle="Reusable ordering rules for proposal output" mainClassName="bg-[#F8FAFC] p-6">
      <div className="hx-proposal-v2 mx-auto w-full max-w-[1600px]">
        <div className="mb-4 flex flex-wrap items-center gap-5 rounded-xl border border-[#E2E8F0] bg-white px-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" style={{ minHeight: 68 }}>
          <Metric label="Saved" value={presets.length} />
          <Metric label="Current Steps" value={form.steps.length} />
          <Metric label="Mode" value={form.id ? "Edit" : "New"} />
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={() => loadPresets(true)} disabled={loading} className="h-9 rounded-lg border-[#CBD5E1] text-[#334155]">
              Refresh
            </Button>
            <Button type="button" onClick={savePreset} disabled={saving} className="h-9 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Save size={15} /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <ProposalTabs />

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
          <section className="rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-[14px] font-bold text-[#0F172A]">Presets</h3>
                <p className="text-xs text-[#64748B]">{loading ? "Loading..." : `${presets.length} saved`}</p>
              </div>
            </div>
            <button type="button" onClick={() => setForm(EMPTY_FORM)} className={`mb-2 flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-semibold ${!form.id ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700"}`}>
              <Plus size={14} /> New preset
            </button>
            {presets.map((preset) => (
              <button key={preset.id} type="button" onClick={() => selectPreset(preset)} className={`mb-2 block w-full rounded-lg border px-3 py-2 text-left ${form.id === preset.id ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                <div className="truncate text-sm font-semibold text-slate-900">{preset.name}</div>
                <div className="mt-0.5 text-xs text-slate-500">{asArray(preset.steps).length} step{asArray(preset.steps).length === 1 ? "" : "s"}</div>
              </button>
            ))}
            {!loading && presets.length === 0 && (
              <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] px-3 py-8 text-center">
                <div className="text-sm font-bold text-[#0F172A]">No presets saved</div>
                <div className="mt-1 text-xs text-[#64748B]">Create a preset to standardize proposal ordering.</div>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E8F0] pb-3">
              <div>
                <h3 className="text-[15px] font-bold text-[#0F172A]">{form.id ? "Edit arrangement preset" : "New arrangement preset"}</h3>
                <p className="text-xs text-[#64748B]">{selectedPreset ? `Last updated ${new Date(selectedPreset.updated_at).toLocaleString("en-IN")}` : "Define matching steps and proposal grouping rules."}</p>
              </div>
              <div className="flex gap-2">
                {form.id && <Button type="button" variant="outline" onClick={deletePreset} disabled={saving} className="h-9 text-blue-600"><Trash2 size={15} /> Delete</Button>}
                <Button type="button" onClick={savePreset} disabled={saving} className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8]"><Save size={15} /> {saving ? "Saving..." : "Save"}</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Preset Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Western Maharashtra Flow" className="h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional internal note" className="h-9" />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-display text-sm font-bold text-slate-900">Ordered Steps</h4>
                  <p className="text-xs text-slate-500">Each selected hoarding joins the first matching step; unmatched sites go to Other Locations.</p>
                </div>
                <Button type="button" variant="outline" onClick={addStep} className="h-9"><Plus size={15} /> Step</Button>
              </div>

              {form.steps.map((step, index) => (
                <div key={index} className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                  <div className="mb-2.5 flex items-center justify-between gap-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Step {index + 1}</div>
                    <div className="flex gap-1">
                      <Button type="button" variant="outline" size="sm" onClick={() => moveStep(index, -1)} disabled={index === 0} className="h-8">Up</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => moveStep(index, 1)} disabled={index === form.steps.length - 1} className="h-8">Down</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => removeStep(index)} disabled={form.steps.length === 1} className="text-blue-600"><Trash2 size={13} /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-6">
                    <div className="space-y-1 xl:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Step Label</Label>
                      <Input value={step.step_label} onChange={(e) => updateStep(index, { step_label: e.target.value })} placeholder="Sangli Downstream" className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">City Label</Label>
                      <Input value={step.match_city_label} onChange={(e) => updateStep(index, { match_city_label: e.target.value })} placeholder="Sangli" className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">Area</Label>
                      <Input value={step.match_area} onChange={(e) => updateStep(index, { match_area: e.target.value })} placeholder="Optional" className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">Location Type</Label>
                      <Input
                        list={`proposal-arrangement-location-types-${index}`}
                        value={step.match_route_segment}
                        onChange={(e) => updateStep(index, { match_route_segment: e.target.value })}
                        placeholder="Any"
                        className="h-9"
                      />
                      <datalist id={`proposal-arrangement-location-types-${index}`}>
                        {LOCATION_TYPES.map((segment) => <option key={segment} value={segment} />)}
                      </datalist>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">Traffic Direction</Label>
                      <Select value={step.match_direction || "any"} onValueChange={(v) => updateStep(index, { match_direction: v === "any" ? "" : v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DIRECTIONS.map(([value, label]) => <SelectItem key={value || "any"} value={value || "any"}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 xl:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Notes</Label>
                      <Input value={step.notes} onChange={(e) => updateStep(index, { notes: e.target.value })} placeholder="Optional" className="h-9" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="min-w-24 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-[#64748B]">{label}</div>
      <div className="mt-1 text-sm font-bold text-[#0F172A]">{value}</div>
    </div>
  );
}
